import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, Input, Output, signal, viewChild } from '@angular/core';
import { AnomalyAnalyzerService } from '@ids-services';
import { CardComponent } from '@microsec/components';
import { BaseComponent } from '@ids-components';
import { FormBuilderComponent } from '@microsec/components';
import { FormItem } from '@microsec/models';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { finalize } from 'rxjs';
import { DOMAIN_TOKEN } from '@microsec/constants';
import { DOCUMENTATION_URL } from '@microsec/constants';
import { FORM_PARAMS } from '../tip-config.component';

@Component({
  selector: 'app-platform-configuration-form',
  templateUrl: './platform-configuration-form.component.html',
  styleUrls: ['./platform-configuration-form.component.scss'],
  imports: [CommonModule, ButtonModule, DividerModule, CardComponent, FormBuilderComponent],
})
export class PlatformConfigurationFormComponent extends BaseComponent {
  @Input() isLoading = signal(false);

  @Input() tipConfig = signal<any>(null);

  @Input() isFirstLoad = signal(true);

  @Output() getConfigDataEvent = new EventEmitter<boolean>();

  fields = signal<FormItem[]>([]);

  form = viewChild<FormBuilderComponent>('fb');

  isDefault = signal(false);

  constructor(public anomalyAnalyzerSrv: AnomalyAnalyzerService) {
    super();
    effect(() => {
      if (!this.isFirstLoad()) {
        this.initForm();
      }
    });
  }

  getConfigData(isDefault: any = false) {
    this.isDefault.set(isDefault);
    this.getConfigDataEvent.emit(isDefault);
  }

  initForm() {
    const fields: FormItem[] = [
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.TIP_URL,
        label: 'Host URL',
        required: true,
        field: 'input',
        rowStyleClass: '50rem',
        placeholder: 'TIP Base URL',
        // eslint-disable-next-line no-useless-escape
        pattern: '^https?:\\/\\/(localhost|(\\d{1,3}\\.){3}\\d{1,3}|(www\\.)?[a-zA-Z0-9\\-\\.]+\\.[a-zA-Z]{2,})(:\\d{1,5})?(\\/)?$',
        isMediumSize: true,
        defaultValue: this.tipConfig()?.[FORM_PARAMS.TIP_URL] || '',
      } as FormItem),
      Object.assign(new FormItem(), {
        hasNoLabel: true,
        field: 'divider',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.API_TOKEN,
        label: 'API Token',
        required: true,
        field: 'password',
        placeholder: 'TIP API Token',
        pattern: '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}', //uuid pattern
        feedback: false,
        isMediumSize: true,
        defaultValue: this.tipConfig()?.[FORM_PARAMS.API_TOKEN] || '',
      } as FormItem),
      Object.assign(new FormItem(), {
        hasNoLabel: true,
        field: 'divider',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.DOWNLOAD_FREQUENCY,
        label: 'Update Frequency (hours)',
        required: true,
        field: 'number',
        isMediumSize: true,
        maxValue: 1000,
        minValue: 1,
        defaultValue: this.tipConfig()?.[FORM_PARAMS.DOWNLOAD_FREQUENCY] || 1,
        fieldInfo: 'The frequency at which MicroIDS downloads data from the Threat Intelligence Platform',
      } as FormItem),
    ];
    this.fields.set(fields);
  }

  onSubmit() {
    this.isLoading.set(true);
    const payload = this.form()?.getRawValue();
    this.anomalyAnalyzerSrv
      .patchTipConfig(payload)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.getConfigData(false);
          this.form()?.form?.markAsPristine();
          this.showSuccessMessage('TIP Configurations are updated successfully');
        },
        error: (err: any) => {
          this.form()?.showServerErrorMessage(err);
          this.showErrorMessage(err);
        },
      });
  }

  goToDocumentation() {
    const customDomain: any = localStorage.getItem('domain');
    window.open(DOCUMENTATION_URL.replace(DOMAIN_TOKEN, customDomain), '_blank');
  }

  shouldDisableSubmit() {
    return this.isDefault() ? false : !this.form()?.valid || this.isLoading() || this.form()?.form?.pristine;
  }
}
